<template lang="pug">
  div
    a-row.mt-4(
      :gutter="30"
    )
      a-col.d-inline.d-lg-none(
        :span="24"
      )
        b-alert(
          show
          variant="secondary"
        )
          | {{ $t('components.login.tpl.access_zone') }}
          i.fa.fa-sign-in.ml-2.d-inline
          br
          small.text-muted
            | {{ $t('components.login.tpl.access_zone_info', { instance: formattedInstance }) }}
      a-col.mb-4(
        :lg="8"
        :md="24"
      )
        a-spin(
          :spinning="loading"
          size="large"
          :tip="$t('components.login.tpl.loading_tip')"
        )
          b-card.login.mx-auto(
            header-tag="header"
          )
            div(
              slot="header"
            )
              | {{ $t('components.login.tpl.login') }}
              button.login__restore-password.small(
                type="button"
                class="btn btn-link p-0 btn-sm"
                @click="handleGotToRestorePasswordRequest"
              ) {{ $t('components.login.tpl.restore_password') }}
            b-form(
              @submit="onSubmit"
            )
              .input-group.mb-3
                input.form-control(
                  v-model="username"
                  @keyup.enter="onSubmit"
                  type="text"
                  :placeholder="$t('components.login.tpl.username')"
                  autofocus
                )
              .input-group.mb-3
                input.form-control(
                  v-model="password"
                  @keyup.enter="onSubmit"
                  :type="showPassword ? 'text': 'password'"
                  :placeholder="$t('components.login.tpl.password')"
                )
                .input-group-append
                  button.btn.btn-outline-primary(
                    type="button"
                    @click="showPassword = !showPassword"
                  )
                    i.fa(
                      :class="showPassword ? 'fa-eye': 'fa-eye-slash'"
                    )
              b-button.btn-block(
                type="button"
                variant="outline-primary"
                @click="onSubmit"
                @keyup.enter="onSubmit"
              ) {{ $t('components.login.tpl.enter') }}
              .pt-3.text-center
                small.text-danger(
                  v-if="message"
                ) {{message}}
      a-col.d-none.d-lg-inline(
        :lg="16"
        :md="24"
      )
        b-jumbotron.login__jumbotron.text-center(
          text-variant="dark"
        )
          template(
            slot="header"
            v-if="instance === 'gecp'"
          )
            a-row()
              a-col(:span="6")
                b-img(
                  left
                  src="/repository/img/gecp_logo.png"
                  class="login__logo-gecp"
                )
              a-col(:span="18")
                h1
                  | {{ $t('components.login.tpl.access_zone') }}
                  i.fa.fa-sign-in.ml-3.d-inline
                h5 {{ $t('components.login.tpl.access_zone_info', {instance: formattedInstance })}}

          template(
            slot="header"
            v-if="instance === 'mfar'"
          )
            h1
              | {{ $t('components.login.tpl.access_zone') }}
              i.fa.fa-sign-in.ml-3.d-inline

          template(
            slot="lead"
            v-if="instance === 'mfar'"
          )
            | {{ $t('components.login.tpl.access_zone_info', { instance: formattedInstance }) }}

          hr.mt-3
          small.text-muted.text-justify
            p
              i
                | {{ $t('components.login.tpl.optimized_for_1') }}
            p
              i.text-muted
                | {{ $t('components.login.tpl.optimized_for_2') }}
                a.ml-1(href="https://www.google.com/intl/es_ALL/chrome/") Chrome
                | ,
                a.ml-1(href="https://www.mozilla.org/es-ES/firefox/new/") Firefox
                |  {{ $t('components.login.tpl.optimized_for_3') }}
                a.ml-1(href="https://www.microsoft.com/es-es/windows/microsoft-edge") Microsoft Edge.
            p
              i
                | {{ $t('components.login.tpl.optimized_for_4') }}

</template>

<script>
import i18n from '@/i18n';
import { mapGetters, createNamespacedHelpers } from 'vuex';

const { mapActions } = createNamespacedHelpers('login');

export default {
  props: {
    instance: {
      type: String,
      default: '',
    },
    redirectUrl: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      username: '',
      password: '',
      message: '',
      loading: false,
      showPassword: false,
    };
  },
  computed: {
    ...mapGetters('login', [
      'FORMATTED_TOKEN',
    ]),
    formattedInstance() {
      return this.instance && this.instance.toUpperCase();
    },
  },
  methods: {
    ...mapActions([
      'doLogin',
    ]),
    async onSubmit() {
      this.loading = true;
      try {
        await this.doLogin({
          instance: this.instance,
          username: this.username,
          password: this.password,
        });

        if (this.redirectUrl) {
          window.location = this.getRedirectUrl();
          return;
        }

        this.$router.push({
          name: 'projects',
          params: {
            instance: this.instance,
          },
        });
      } catch (error) {
        if (error.response && error.response.status === 401) {
          const {
            unauthorizedEntity,
            resourceUrl,
          } = error.response.data;

          if (!resourceUrl) {
            switch (unauthorizedEntity) {
              case 'USER-INCORRECT_USERNAME_OR_PWD':
                this.message = this.$t('components.login.js["USER-INCORRECT_USERNAME_OR_PWD"]');
                break;
              case 'USER-PWD_EXPIRED':
                this.message = this.$t('components.login.js["USER-PWD_EXPIRED"]');
                break;
              default:
                this.message = this.$t('components.login.js.ACCESS_DENIED');
                break;
            }
            return;
          }

          window.location = resourceUrl;
        }
      } finally {
        this.loading = false;
      }
    },
    handleGotToRestorePasswordRequest() {
      let path = `/${this.instance}/restore-password-request`;

      if (i18n.locale !== 'es') {
        path = `${path}/${i18n.locale}`;
      }

      this.$router.push({
        path,
        query: {
          redirectUrl: encodeURIComponent(window.location.href),
          autoLogin: '1',
        },
      });
    },
    getRedirectUrl() {
      return `${this.redirectUrl}&token=${this.FORMATTED_TOKEN}`;
    },
  },
};
</script>

<style scoped>
.login__jumbotron {
  padding-top: 30px;
  padding-bottom: 10px;
  background-color: hsl(0, 0%, 95%);
}
.login__restore-password {
  float: right;
}
.login__logo-gecp {
  width: 151px;
}
</style>
